import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { useLocation, useNavigationType, useSearchParams } from 'react-router-dom'
import { glassBackground, nearbyProfileScrollerTop, size } from '@styles/theme'
import useCurrentLocationContext from '@contexts/currentLocationContext'
import { thumbnailLargeNoFallback } from '@utils/thumbnails'
import usePostStatsContext from '@contexts/postStatsContext'
import { hasAtAGlance, hasStats } from '@utils/formatStat'
import { useCableContext } from '@contexts/cableContext'
import { localAccessors } from '@utils/localStateAccess'
import { useAuthContext } from '@contexts/authContext'
import useModalContext from '@contexts/modalContext'
import searchParamsHas from '@utils/searchParamsHas'
import { MapView } from '@contexts/mapDataContext'
import useStateRef from 'react-usestateref'
import axios from '@utils/webRequests'
import useFeed from '@hooks/useFeed'
import styled from '@emotion/styled'

import NearbyProfileTopbar from '@components/nearby/NearbyProfileTopbar'
import ProfileRecentPosts from '@components/nearby/ProfileRecentPosts'
import ProfileUserBrief from '@components/nearby/ProfileUserBrief'
import ProfileAtAGlance from '@components/nearby/ProfileAtAGlance'
import ProfileStats from '@components/nearby/ProfileStats'
import AnimationImage from '@elements/AnimationImage'
import ModalContainer from '@elements/ModalContainer'
import { PiUserFill } from 'react-icons/pi'

const BackgroundImageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  background-color: black;
`

const BackgroundImage = styled(AnimationImage)`
  width: 100dvw;
  height: 80dvh;
`

const ImageGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: linear-gradient(0deg, rgba(0,0,0,1) 1%, rgba(0,0,0,0) 50%, rgba(255,255,255,0) 100%);
`

const NoImageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100dvw;
  height: 80dvh;
  padding-top: 15dvh;
  background-color: #141414;
`

const ContentContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;

  display: flex;
  flex-direction: column;

  width: 100dvw;
  height: 100dvh;

  overflow-y: scroll;
  overflow-x: hidden;
`

const ScrollerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${size(2)};
  width: 100%;
  z-index: 1;
  
  border-radius: 25px 25px 0 0;
  border-top: 2px solid rgba(255,255,255, 0.15);
  // animation: fadeInFromBottom 250ms ease-in;

  ${glassBackground()}
`

const MainImageArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  flex-shrink: 0;

  height: calc(100dvh - ${nearbyProfileScrollerTop});
  width: 100%;
  padding-right: ${size(1)};
`

const EndSpace = styled.div`
  height: ${size(15)};
  width: 100%;
`

const modalBackgroundStyles = {
  backgroundColor: 'black',
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100dvh',
  width: '100dvw',
  overflowY: 'scroll',
}

const NearbyProfile = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const postStats = usePostStatsContext()
  const { currentLocation } = useCurrentLocationContext()
  const { emitActivityPulse } = useCableContext()
  const { state: authState } = useAuthContext()
  const { nearbyProfile } = useModalContext()
  const [user, setUser] = useState(null)
  const location = useLocation()
  const feedState = useFeed({
    getPostsUrl: `/api/users/${user?.uuid}/posts?limit=10&omit_reposts=true`,
    feedAccessor: localAccessors.nearbyProfilePosts(user?.uuid),
    postStats,
  })
  const {
    posts,
    postsLoaded,
    setPostsLoaded,
  } = feedState

  const [userHasStats, setUserHasStats] = useState(false)
  const [userHasAtAGlance, setUserHasAtAGlance] = useState(false)
  const [scrollEl, setScrollEl, scrollElRef] = useStateRef(null)
  const navigationType = useNavigationType()
  const hasMainImage = !!(user?.nearbyMedia?.thumbnails?.[0])
  const userUuid = searchParams.get('nearby_uuid')

  // Sets the scroll position to where the user was previously on the page when back-navigating
  useEffect(() => {
    // TODO: Attempt to refactor this into some kind of hook extendable for use with virtuoso since that's another use case of ours.
    if (userUuid && nearbyProfile?.isOpen && postsLoaded && scrollEl) {
      if (navigationType === 'POP') {
        const scrollPos = getScrollPos()
        if (scrollPos && scrollElRef.current) {
          setTimeout(() => {
            // WARNING: I have no idea why this need a setTimeout, but I've spent
            //          too much time trying to figure it out and need to move on...
            scrollElRef.current.scrollTop = scrollPos
          })
        }
      }
    }

    return () => {
      if (scrollElRef.current?.scrollTop) {
        setScrollPos(scrollElRef?.current?.scrollTop)
      }
    }
  }, [userUuid, postsLoaded, nearbyProfile?.isOpen, scrollEl])

  const getScrollPos = () => {
    try {
      return JSON.parse(sessionStorage.getItem(`nearby-profile-scrollTop-${userUuid}`))
    } catch (err) {
      console.log(err)
      return 0
    }
  }

  const setScrollPos = (scrollTop) => {
    sessionStorage.setItem(`nearby-profile-scrollTop-${userUuid}`, scrollTop || 0)
  }
  
  // Sets user on initial render, either through session storage, or through location state
  // NOTE: If we don't set the user to state at all, the data leaves before the exit animation,
  //       and the rendering on exit can get pretty weird.
  useLayoutEffect(() => {
    if (user || !nearbyProfile.isOpen || !authState?.user) return;
    const userState = location?.state?.user && typeof location.state.user === 'string' ? JSON.parse(location?.state?.user) : null
    const isCurrentUser = authState?.user?.nearbyUuid === userUuid
   
    // This attempts to find temporary user data when the modal first opens, or sets the user to self
    if (isCurrentUser) {
      setUser({ ...authState?.user, lonlat: currentLocation })
    } else if (userState) {
      setUser(userState)
      emitActivityPulse()
    } else {
      let oldUser = JSON.parse(sessionStorage.getItem(`nearby-profile-${userState?.uuid || userUuid}`) || null)
      if (oldUser) setUser(oldUser)
      if (!isCurrentUser) {
        emitActivityPulse()
      }
    }

    // Unlike the above, this gets the data from the backend when the modal opens to ensure we're seeing the most up to date information
    // TODO: We want to update the map profile with information provided if the user is on the map
    // TODO: We also need to handle the loading of the data if it takes a while, and there's no previous user set. Right now, it just looks like a blank profile when nothing is loaded
    if (!isCurrentUser && userUuid) {
      axios.get(`/api/users/${userUuid}/nearby_profile`)
        .then((res) => {
          if (!res?.data?.nearbyUuid) return;
          MapView.updateUser(res.data.nearbyUuid, { ...res.data })
          setUser(res.data)
        })
        .catch(console.log)
    }
  }, [nearbyProfile.isOpen, authState.user])

  // This sets the session user state for potential later use
  useEffect(() => {
    if (user?.nearbyUuid) {
      sessionStorage.setItem(`nearby-profile-${user?.nearbyUuid}`, JSON.stringify(user))
    }
  }, [user])

  // This cleans up state when the modal gets closed
  useEffect(() => {
    if (!nearbyProfile.isOpen) {
      setTimeout(() => {
        // Short ciruit if user navigated back to the same screen. This prevents us from displaying an empty profile when we shouldn't
        if (searchParamsHas('screen_modal') && searchParamsHas('nearby_uuid', user?.nearbyUuid)) return;
        setPostsLoaded(false)
        setUser(null)
        setUserHasStats(false)
        setUserHasAtAGlance(false)
      }, 250)
    }
  }, [nearbyProfile.isOpen])

  // Determines if the user has stats so we can display the proper message on the profile
  useEffect(() => {
    if (user?.nearbyUuid && nearbyProfile.isOpen) {
      setUserHasStats(hasStats(user?.extendedInfos))
      setUserHasAtAGlance(hasAtAGlance(user?.extendedInfos))
    }
  }, [user?.nearbyUuid, nearbyProfile.isOpen])

  const renderUserImage = () => (
    <BackgroundImageContainer>
      <BackgroundImage src={thumbnailLargeNoFallback(user?.nearbyMedia?.thumbnails?.[0])} />
      <ImageGradient />
    </BackgroundImageContainer>
  )
  
  const renderNoUserImage = () => (
    <NoImageContainer>
      <PiUserFill size={size(25)} fill='black' />
      <ImageGradient />
    </NoImageContainer>
  )

  const renderImageViewer = (e) => {
    if (e.target.id === 'nearby-profile_main_image' && hasMainImage) {
      searchParams.set('nearby_image_viewer', 'nearby-image-viewer-open')
      setSearchParams(searchParams)
    }
  }

  const handleRenderCustomHeader = () => (
    <NearbyProfileTopbar
      postsLoaded={postsLoaded}
      setUser={(user) => {
        sessionStorage.setItem(`nearby-profile-${user?.nearbyUuid}`, user)
        setUser(user)
      }}
      user={user}
    />
  )

  return(
    <ModalContainer
      backgroundStyles={modalBackgroundStyles}
      open={nearbyProfile.isOpen}
      renderCustomHeader={handleRenderCustomHeader}>
      {hasMainImage
        ? renderUserImage()
        : renderNoUserImage()
      }
      <ContentContainer
        ref={(node) => node && setScrollEl(node)}
        id='nearby-profile'>
        <MainImageArea
          id='nearby-profile_main_image'
          onClick={renderImageViewer}>
          <ProfileUserBrief user={user} />
        </MainImageArea>
        <ScrollerContainer>
          <ProfileAtAGlance
            user={user}
            userHasAtAGlance={userHasAtAGlance}
            postCount={user?.postCount}
          />
          {userHasStats && <ProfileStats user={user} />}
          <ProfileRecentPosts
            userHasStats={userHasStats}
            feedState={feedState}
            user={user}
          />
          <EndSpace />
        </ScrollerContainer>
      </ContentContainer>
    </ModalContainer>
  )
}

export default NearbyProfile